import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Button,
  TableContainer,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  SimpleGrid,
  Card,
  CardBody,
  Icon,
  Divider,
  Badge,
  Spinner,
  Center,
  Text,
} from "@chakra-ui/react";
import {
  FaDollarSign,
  FaTruck,
  FaPercentage,
  FaMapMarkerAlt,
  FaSave,
  FaArrowLeft,
  FaLock,
  FaCheck,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const SimulacaoPlanilha = () => {
  // Estados para os custos
  const [maoDeObra, setMaoDeObra] = useState(0.3);
  const [pneu, setPneu] = useState(0.02);
  const [manutencao, setManutencao] = useState(0.81);
  const [custoVariavel, setCustoVariavel] = useState(0);
  const [custoFixo, setCustoFixo] = useState(0.085);
  const [s10, setS10] = useState(5.34);
  const [s500, setS500] = useState(5.24);
  const [mediaConsumo, setMediaConsumo] = useState(0);
  const [margem, setMargem] = useState(0.2);
  const [desconto7, setDesconto7] = useState(0.05);
  const [desconto14, setDesconto14] = useState(0.04);
  const [desconto21, setDesconto21] = useState(0.02);
  const [campoGrande10, setCampoGrande10] = useState(0);
  const [campoGrande500, setCampoGrande500] = useState(0);
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Função para calcular os custos totais
  const calcularTotais = () => {
    const custoTotal =
      custoFixo + custoVariavel + maoDeObra + pneu + manutencao;
    return custoTotal.toFixed(4);
  };

  useEffect(() => {
    const totalCustoVariavel = mediaConsumo + maoDeObra + pneu + manutencao;
    setCustoVariavel(parseFloat(totalCustoVariavel.toFixed(4))); // Arredonda para 4 casas decimais
  }, [mediaConsumo, maoDeObra, pneu, manutencao]);

  useEffect(() => {
    const media = s10 / 4;
    setMediaConsumo(media);
  }, [s10]);

  useEffect(() => {
    const campoGrandeS10 = custoFixo + s10 + margem;
    setCampoGrande10(campoGrandeS10);
  }, [custoFixo, s10, margem]);

  useEffect(() => {
    const campoGrandeS500 = custoFixo + s500 + margem;
    setCampoGrande500(campoGrandeS500);
  }, [custoFixo, s500, margem]);

  const [data, setData] = useState([
    { kilometragem: 100 },
    { kilometragem: 200 },
    { kilometragem: 300 },
    { kilometragem: 400 },
    { kilometragem: 500 },
    { kilometragem: 600 },
    { kilometragem: 700 },
    { kilometragem: 800 },
    { kilometragem: 900 },
  ]);

  const handleKilometragemChange = (value, index) => {
    const updatedData = [...data];
    updatedData[index].kilometragem = value; // Atualiza a kilometragem no índice específico
    setData(updatedData); // Atualiza o estado
  };

  const calculateTocoRural = (kilometragem) => {
    return (kilometragem * custoVariavel) / 12000 + custoFixo;
  };

  const calculateTruckCidade = (kilometragem) => {
    return (kilometragem * custoVariavel) / 20000 + custoFixo;
  };

  const custoVendaS10 = () => {
    return s10 + calculateTocoRural + margem;
  };

  useEffect(() => {
    let cancelled = false;
    let settleTimer;

    const fetchPrices = async () => {
      setIsLoadingResults(true);
      try {
        const res = await fetch("/api/get-prices");
        const data = await res.json();
        if (cancelled) return;
        if (data.s10) setS10(data.s10);
        if (data.s500) setS500(data.s500);
      } catch (error) {
        console.error("Erro ao buscar preços:", error);
      } finally {
        // Pequeno delay para a UI estabilizar os totais recalculados
        settleTimer = setTimeout(() => {
          if (!cancelled) setIsLoadingResults(false);
        }, 280);
      }
    };
    fetchPrices();

    return () => {
      cancelled = true;
      if (settleTimer) clearTimeout(settleTimer);
    };
  }, []);

  return (
    <Box
      minH="100vh"
      bg="gray.50"
      py={{ base: 4, md: 8 }}
      px={{ base: 4, md: 6 }}
    >
      <Box maxW={{ base: "100%", lg: "1400px" }} mx="auto">
        {/* Header com Botão de Navegação */}
        <Flex
          align="center"
          justify="space-between"
          mb={{ base: 6, md: 8 }}
          flexWrap="wrap"
          gap={4}
        >
          <Button
            onClick={() => {
              try {
                navigate("/home");
              } catch (error) {
                console.error("Erro ao navegar para home:", error);
                navigate("/");
              }
            }}
            leftIcon={<FaArrowLeft />}
            variant="outline"
            size={{ base: "sm", md: "md" }}
            borderColor="#007abe"
            color="#007abe"
            _hover={{
              transform: "translateX(-4px)",
              boxShadow: "md",
              bg: "#007abe",
              color: "white",
            }}
            transition="all 0.2s"
          >
            Voltar
          </Button>

          <Heading
            as="h1"
            size={{ base: "lg", md: "xl", lg: "2xl" }}
            textAlign="center"
            flex="1"
            color="#007abe"
            fontWeight="bold"
          >
            Simulação de Custos de Transporte
          </Heading>

          <Box w={{ base: "100%", md: "auto" }} />
        </Flex>

        {isLoadingResults ? (
          <Center
            minH="420px"
            flexDirection="column"
            gap={4}
            py={16}
            bg="white"
            borderRadius="xl"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <Spinner
              thickness="4px"
              speed="0.7s"
              emptyColor="gray.200"
              color="#007abe"
              size="xl"
            />
            <Text color="gray.600" fontSize="sm" fontWeight="medium">
              Carregando e plotando resultados...
            </Text>
          </Center>
        ) : (
          <>
        {/* Grid de Cards Responsivo */}
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 4 }}
          spacing={{ base: 4, md: 6 }}
          mb={8}
        >
          {/* Bloco 1: Custos */}
          <Card
            bg="white"
            borderRadius="xl"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            _hover={{
              transform: "translateY(-4px)",
              boxShadow: "lg",
              borderColor: "#007abe",
            }}
            transition="all 0.3s"
            h="fit-content"
          >
            <CardBody p={6}>
              <Flex align="center" mb={4}>
                <Icon as={FaDollarSign} color="#c0a316" boxSize={6} mr={2} />
                <Heading size="md" color="#007abe" fontWeight="bold">
                  Custos
                </Heading>
              </Flex>
              <Divider mb={4} borderColor="gray.200" />
              <Flex direction="column" gap={4}>
                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    Mão de Obra
                  </FormLabel>
                  <NumberInput
                    bg="gray.50"
                    value={maoDeObra}
                    onChange={(value) => setMaoDeObra(parseFloat(value) || 0)}
                    min={0}
                    precision={4}
                    size="md"
                  >
                    <NumberInputField
                      _focus={{
                        borderColor: "#007abe",
                        boxShadow: "0 0 0 1px #007abe",
                      }}
                    />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    Pneu
                  </FormLabel>
                  <NumberInput
                    bg="gray.50"
                    value={pneu}
                    onChange={(value) => setPneu(parseFloat(value) || 0)}
                    min={0}
                    precision={4}
                    size="md"
                  >
                    <NumberInputField
                      _focus={{
                        borderColor: "#007abe",
                        boxShadow: "0 0 0 1px #007abe",
                      }}
                    />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    Manutenção
                  </FormLabel>
                  <NumberInput
                    bg="gray.50"
                    value={manutencao}
                    onChange={(value) => setManutencao(parseFloat(value) || 0)}
                    min={0}
                    precision={4}
                    size="md"
                  >
                    <NumberInputField
                      _focus={{
                        borderColor: "#007abe",
                        boxShadow: "0 0 0 1px #007abe",
                      }}
                    />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    Custo Fixo (por litro)
                  </FormLabel>
                  <NumberInput
                    bg="gray.50"
                    value={custoFixo}
                    onChange={(value) => setCustoFixo(parseFloat(value) || 0)}
                    min={0}
                    precision={4}
                    size="md"
                  >
                    <NumberInputField
                      _focus={{
                        borderColor: "#007abe",
                        boxShadow: "0 0 0 1px #007abe",
                      }}
                    />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    Custo Variável (por km)
                  </FormLabel>
                  <NumberInput
                    bg="gray.100"
                    value={custoVariavel}
                    onChange={(value) =>
                      setCustoVariavel(parseFloat(value) || 0)
                    }
                    min={0}
                    precision={4}
                    isReadOnly
                    size="md"
                  >
                    <NumberInputField fontWeight="bold" color="#007abe" />
                  </NumberInput>
                </FormControl>
              </Flex>
            </CardBody>
          </Card>

          {/* Bloco 2: Custo Mercadoria */}
          <Card
            bg="white"
            borderRadius="xl"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            _hover={{
              transform: "translateY(-4px)",
              boxShadow: "lg",
              borderColor: "#007abe",
            }}
            transition="all 0.3s"
            h="fit-content"
          >
            <CardBody p={6}>
              <Flex align="center" mb={4}>
                <Icon as={FaTruck} color="#c0a316" boxSize={6} mr={2} />
                <Heading size="md" color="#007abe" fontWeight="bold">
                  Custo Mercadoria
                </Heading>
              </Flex>
              <Divider mb={4} borderColor="gray.200" />
              <Flex direction="column" gap={4}>
                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    S10
                  </FormLabel>
                  <Input
                    bg="gray.50"
                    type="number"
                    value={s10}
                    onChange={(e) => setS10(parseFloat(e.target.value) || 0)}
                    size="md"
                    _focus={{
                      borderColor: "#007abe",
                      boxShadow: "0 0 0 1px #007abe",
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    S500
                  </FormLabel>
                  <Input
                    bg="gray.50"
                    type="number"
                    value={s500}
                    onChange={(e) => setS500(parseFloat(e.target.value) || 0)}
                    size="md"
                    _focus={{
                      borderColor: "#007abe",
                      boxShadow: "0 0 0 1px #007abe",
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    Margem
                  </FormLabel>
                  <Input
                    bg="gray.50"
                    type="number"
                    value={margem}
                    onChange={(e) => setMargem(parseFloat(e.target.value) || 0)}
                    size="md"
                    _focus={{
                      borderColor: "#007abe",
                      boxShadow: "0 0 0 1px #007abe",
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    Média Consumo
                  </FormLabel>
                  <Input
                    bg="gray.100"
                    type="number"
                    value={mediaConsumo}
                    readOnly
                    size="md"
                    fontWeight="bold"
                    color="#007abe"
                  />
                </FormControl>

                <Button
                  bg="#007abe"
                  color="white"
                  mt={4}
                  onClick={onOpen}
                  leftIcon={<FaSave />}
                  size="md"
                  _hover={{
                    transform: "scale(1.02)",
                    bg: "#0066a3",
                  }}
                  transition="all 0.2s"
                >
                  Salvar preços
                </Button>
              </Flex>
            </CardBody>
          </Card>

          {/* Bloco 3: Descontos */}
          <Card
            bg="white"
            borderRadius="xl"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            _hover={{
              transform: "translateY(-4px)",
              boxShadow: "lg",
              borderColor: "#007abe",
            }}
            transition="all 0.3s"
            h="fit-content"
          >
            <CardBody p={6}>
              <Flex align="center" mb={4}>
                <Icon as={FaPercentage} color="#c0a316" boxSize={6} mr={2} />
                <Heading size="md" color="#007abe" fontWeight="bold">
                  Descontos
                </Heading>
              </Flex>
              <Divider mb={4} borderColor="gray.200" />
              <Flex direction="column" gap={4}>
                <FormControl>
                  <FormLabel
                    color="gray.700"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    <Badge bg="#c0a316" color="white" mr={2}>
                      07
                    </Badge>{" "}
                    DIAS
                  </FormLabel>
                  <Input
                    bg="gray.50"
                    type="number"
                    value={desconto7}
                    onChange={(e) =>
                      setDesconto7(parseFloat(e.target.value) || 0)
                    }
                    size="md"
                    _focus={{
                      borderColor: "#007abe",
                      boxShadow: "0 0 0 1px #007abe",
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel
                    color="gray.700"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    <Badge bg="#c0a316" color="white" mr={2}>
                      14
                    </Badge>{" "}
                    DIAS
                  </FormLabel>
                  <Input
                    bg="gray.50"
                    type="number"
                    value={desconto14}
                    onChange={(e) =>
                      setDesconto14(parseFloat(e.target.value) || 0)
                    }
                    size="md"
                    _focus={{
                      borderColor: "#007abe",
                      boxShadow: "0 0 0 1px #007abe",
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel
                    color="gray.700"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    <Badge bg="#c0a316" color="white" mr={2}>
                      21
                    </Badge>{" "}
                    DIAS
                  </FormLabel>
                  <Input
                    bg="gray.50"
                    type="number"
                    value={desconto21}
                    onChange={(e) =>
                      setDesconto21(parseFloat(e.target.value) || 0)
                    }
                    size="md"
                    _focus={{
                      borderColor: "#007abe",
                      boxShadow: "0 0 0 1px #007abe",
                    }}
                  />
                </FormControl>
              </Flex>
            </CardBody>
          </Card>

          {/* Bloco 4: Campo Grande */}
          <Card
            bg="white"
            borderRadius="xl"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            _hover={{
              transform: "translateY(-4px)",
              boxShadow: "lg",
              borderColor: "#007abe",
            }}
            transition="all 0.3s"
            h="fit-content"
          >
            <CardBody p={6}>
              <Flex align="center" mb={4}>
                <Icon as={FaMapMarkerAlt} color="#c0a316" boxSize={6} mr={2} />
                <Heading size="md" color="#007abe" fontWeight="bold">
                  Campo Grande
                </Heading>
              </Flex>
              <Divider mb={4} borderColor="gray.200" />
              <Flex direction="column" gap={4}>
                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    S10
                  </FormLabel>
                  <Input
                    bg="gray.100"
                    isReadOnly
                    type="number"
                    value={campoGrande10}
                    onChange={(e) =>
                      setCampoGrande10(parseFloat(e.target.value) || 0)
                    }
                    size="md"
                    fontWeight="bold"
                    color="#007abe"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel
                    color="#c0a316"
                    fontWeight="semibold"
                    fontSize="sm"
                  >
                    S500
                  </FormLabel>
                  <Input
                    bg="gray.100"
                    isReadOnly
                    type="number"
                    value={parseFloat(campoGrande500).toFixed(3)}
                    onChange={(e) =>
                      setCampoGrande500(parseFloat(e.target.value) || 0)
                    }
                    size="md"
                    fontWeight="bold"
                    color="#007abe"
                  />
                </FormControl>
              </Flex>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Tabela Responsiva */}
        <Card
          bg="white"
          borderRadius="xl"
          boxShadow="xl"
          border="1px solid"
          borderColor="gray.200"
          mt={8}
          position="relative"
          overflow="hidden"
        >
          <CardBody p={6}>
            <Heading size="md" mb={6} color="gray.700" fontWeight="bold">
              Tabela de Simulação
            </Heading>
            <TableContainer
              overflowX="auto"
              sx={{
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "gray.100",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#007abe",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#0066a3",
                  },
                },
              }}
            >
              <Table variant="simple" size={{ base: "sm", md: "md" }}>
                <Thead>
                  <Tr bg="#007abe">
                    <Th
                      color="white"
                      fontWeight="bold"
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      KM (IDA/VOLTA)
                    </Th>
                    <Th
                      color="white"
                      fontWeight="bold"
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      TOCO (RURAL)
                    </Th>
                    <Th
                      color="white"
                      fontWeight="bold"
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      TRUCK (CIDADE)
                    </Th>
                    <Th
                      color="white"
                      fontWeight="bold"
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      S10 - TOCO
                    </Th>
                    <Th
                      color="white"
                      fontWeight="bold"
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      S10 - TRUCK
                    </Th>
                    <Th
                      color="white"
                      fontWeight="bold"
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      S500 - TOCO
                    </Th>
                    <Th
                      color="white"
                      fontWeight="bold"
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      S500 - TRUCK
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((row, index) => (
                    <Tr
                      key={index}
                      _hover={{ bg: "gray.50" }}
                      transition="background 0.2s"
                      _even={{ bg: "gray.25" }}
                    >
                      <Td>
                        <Input
                          bg="white"
                          value={row.kilometragem}
                          size="sm"
                          width={{ base: "80px", md: "100px" }}
                          onChange={(e) =>
                            handleKilometragemChange(e.target.value, index)
                          }
                          _focus={{
                            borderColor: "#007abe",
                            boxShadow: "0 0 0 1px #007abe",
                          }}
                        />
                      </Td>
                      <Td fontWeight="medium">
                        {calculateTocoRural(row.kilometragem).toFixed(4)}
                      </Td>
                      <Td fontWeight="medium">
                        {calculateTruckCidade(row.kilometragem).toFixed(4)}
                      </Td>
                      <Td fontWeight="semibold" color="#007abe">
                        {(
                          s10 +
                          calculateTocoRural(row.kilometragem) +
                          margem
                        ).toFixed(4)}
                      </Td>
                      <Td fontWeight="semibold" color="#007abe">
                        {(
                          s10 +
                          calculateTruckCidade(row.kilometragem) +
                          margem
                        ).toFixed(4)}
                      </Td>
                      <Td fontWeight="semibold" color="#007abe">
                        {(
                          s500 +
                          calculateTocoRural(row.kilometragem) +
                          margem
                        ).toFixed(4)}
                      </Td>
                      <Td fontWeight="semibold" color="#007abe">
                        {(
                          s500 +
                          calculateTruckCidade(row.kilometragem) +
                          margem
                        ).toFixed(4)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
          </>
        )}
      </Box>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size={{ base: "sm", md: "md" }}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" boxShadow="2xl">
          <ModalHeader color="#007abe" fontWeight="bold" fontSize="xl">
            Confirmar alteração
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <FormControl>
              <FormLabel fontWeight="semibold" mb={2}>
                <Icon as={FaLock} mr={2} color="#007abe" />
                Digite a senha
              </FormLabel>
              <Input
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                size="lg"
                _focus={{
                  borderColor: "#007abe",
                  boxShadow: "0 0 0 1px #007abe",
                }}
                placeholder="Digite sua senha"
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              _hover={{ bg: "gray.100" }}
            >
              Cancelar
            </Button>
            <Button
              bg="#007abe"
              color="white"
              isLoading={loading}
              leftIcon={<FaCheck />}
              _hover={{ bg: "#0066a3" }}
              onClick={async () => {
                setLoading(true);
                try {
                  const res = await fetch("/api/update-prices", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ s10, s500, password: senha }),
                  });
                  const data = await res.json();
                  if (res.ok) {
                    toast({
                      title: "Sucesso",
                      description: data.message,
                      status: "success",
                      duration: 4000,
                      isClosable: true,
                    });
                    setSenha("");
                    onClose();
                  } else {
                    // Garantir que o erro seja sempre uma string
                    let errorMessage = "Falha ao salvar valores.";
                    if (data.error) {
                      if (typeof data.error === "string") {
                        errorMessage = data.error;
                      } else if (data.error.message) {
                        errorMessage = data.error.message;
                      } else if (data.error.code) {
                        errorMessage = `Erro ${data.error.code}: ${
                          data.error.message ||
                          "Token inválido ou sem permissões"
                        }`;
                      } else {
                        errorMessage = JSON.stringify(data.error);
                      }
                    }

                    toast({
                      title: "Erro",
                      description: errorMessage,
                      status: "error",
                      duration: 4000,
                      isClosable: true,
                    });
                  }
                } catch (err) {
                  toast({
                    title: "Erro",
                    description: "Erro ao conectar com o servidor.",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                  });
                } finally {
                  setLoading(false);
                }
              }}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SimulacaoPlanilha;
